import {
  DeliverabilityReason,
  LineType,
  NumberHealthStatus,
  SmsState,
} from '#/shared/api/generated/communication/models'
import type { NumberHealthRead } from '#/shared/api/generated/communication/models'

/** Visual severity shared by the badge, tooltip values, and composer banner. */
export type HealthTone = 'ok' | 'warn' | 'bad' | 'neutral' | 'muted'

/**
 * Row badge next to a phone number.
 *  - `healthy` shows nothing (call + text both OK).
 *  - `sms_blocked` → amber "call only" glyph.
 *  - `invalid` → red "both channels down" glyph.
 */
export interface HealthBadge {
  tone: 'warn' | 'bad'
  /** i18n key for the screen-reader / legend label. */
  labelKey: string
}

/**
 * The deliverability fields riding inline on customer objects — structurally
 * shared by the shipment service's `CustomerBrief` and the communication
 * service's `CustomerSummary`. A missing `sms_deliverable` means deliverable
 * (the backfill may not have reached the record yet).
 */
export interface CustomerDeliverability {
  sms_deliverable?: boolean
  deliverability_reason?: DeliverabilityReason | null
}

/** Badge for a known customer, straight from the inline fields — no
 *  number-health lookup involved. */
export function getCustomerHealthBadge(
  customer: CustomerDeliverability | null | undefined,
): HealthBadge | null {
  if (customer?.sms_deliverable !== false) return null
  return customer.deliverability_reason === DeliverabilityReason.invalid_number
    ? { tone: 'bad', labelKey: 'numberHealth.badge.invalid' }
    : { tone: 'warn', labelKey: 'numberHealth.badge.smsBlocked' }
}

export function getHealthBadge(
  health: NumberHealthRead | undefined,
): HealthBadge | null {
  if (!health) return null
  if (health.status === NumberHealthStatus.sms_blocked) {
    return { tone: 'warn', labelKey: 'numberHealth.badge.smsBlocked' }
  }
  if (health.status === NumberHealthStatus.invalid) {
    return { tone: 'bad', labelKey: 'numberHealth.badge.invalid' }
  }
  return null
}

const LINE_TYPE_KEY: Record<LineType, string> = {
  [LineType.mobile]: 'numberHealth.lineType.mobile',
  [LineType.landline]: 'numberHealth.lineType.landline',
  [LineType.voip]: 'numberHealth.lineType.voip',
  [LineType.unknown]: 'numberHealth.lineType.unknown',
}

/** The SMS row's resolved value + tone. `iso` carries the opt-out date. */
export interface SmsDescriptor {
  key: string
  tone: HealthTone
  iso: string | null
}

function smsDescriptor(health: NumberHealthRead): SmsDescriptor {
  switch (health.sms_state) {
    case SmsState.deliverable:
      return { key: 'numberHealth.value.deliverable', tone: 'ok', iso: null }
    case SmsState.not_supported:
      return { key: 'numberHealth.value.notSupported', tone: 'warn', iso: null }
    case SmsState.opted_out:
      return {
        key: 'numberHealth.value.optedOut',
        tone: 'warn',
        iso: health.opted_out_at ?? null,
      }
    default:
      return { key: 'numberHealth.value.undeliverable', tone: 'bad', iso: null }
  }
}

/** The four states the tooltip header speaks in — picks its glyph + summary. */
export type HealthKind = 'healthy' | 'landline' | 'optedOut' | 'invalid'

interface TitleModel {
  key: string
  tone: HealthTone
  kind: HealthKind
}

function titleFor(health: NumberHealthRead): TitleModel {
  if (health.status === NumberHealthStatus.invalid) {
    return {
      key: 'numberHealth.tooltip.title.invalid',
      tone: 'bad',
      kind: 'invalid',
    }
  }
  if (health.status === NumberHealthStatus.sms_blocked) {
    return health.sms_state === SmsState.opted_out
      ? {
          key: 'numberHealth.tooltip.title.optedOut',
          tone: 'warn',
          kind: 'optedOut',
        }
      : {
          key: 'numberHealth.tooltip.title.landline',
          tone: 'warn',
          kind: 'landline',
        }
  }
  return {
    key: 'numberHealth.tooltip.title.healthy',
    tone: 'ok',
    kind: 'healthy',
  }
}

/** Everything the hover tooltip renders, resolved from one health record. */
export interface TooltipModel {
  title: TitleModel
  lineTypeKey: string
  carrier: string | null
  voice: { key: string; tone: HealthTone }
  sms: SmsDescriptor
  verified: { labelKey: string; iso: string | null }
}

export function getTooltipModel(health: NumberHealthRead): TooltipModel {
  const invalid = health.status === NumberHealthStatus.invalid
  return {
    title: titleFor(health),
    lineTypeKey: LINE_TYPE_KEY[health.line_type],
    carrier: health.carrier ?? null,
    voice: health.voice_deliverable
      ? { key: 'numberHealth.value.deliverable', tone: 'ok' }
      : { key: 'numberHealth.value.undeliverable', tone: 'bad' },
    sms: smsDescriptor(health),
    verified: {
      // A dead number was never verified — the tooltip says "Last checked".
      labelKey: invalid
        ? 'numberHealth.tooltip.lastChecked'
        : 'numberHealth.tooltip.lastVerified',
      iso: health.last_verified_at ?? null,
    },
  }
}

/** Composer banner shown at the point of send when SMS can't leave. */
export interface ComposerBanner {
  tone: 'warn' | 'bad'
  kind: 'landline' | 'optedOut' | 'invalid'
  iso: string | null
}

export function getComposerBanner(
  health: NumberHealthRead | undefined,
): ComposerBanner | null {
  if (!health || health.sms_state === SmsState.deliverable) return null
  if (
    health.status === NumberHealthStatus.invalid ||
    health.sms_state === SmsState.undeliverable
  ) {
    return { tone: 'bad', kind: 'invalid', iso: null }
  }
  if (health.sms_state === SmsState.not_supported) {
    return { tone: 'warn', kind: 'landline', iso: null }
  }
  return { tone: 'warn', kind: 'optedOut', iso: health.opted_out_at ?? null }
}

/**
 * Composer banner for a known customer, from the inline fields plus the
 * thread's `is_opted_out` flag. Opt-out (STOP) is not part of
 * `deliverability_reason` — it arrives separately on the conversation detail —
 * but it outranks landline/invalid in the banner, matching the send-time
 * rejection order (blocked > opted out > undeliverable). Blocked isn't handled
 * here: a blocked thread swaps the whole composer for the Unblock bar.
 */
export function getInlineComposerBanner({
  customer,
  optedOut = false,
}: {
  customer: CustomerDeliverability | null | undefined
  optedOut?: boolean
}): ComposerBanner | null {
  if (optedOut) return { tone: 'warn', kind: 'optedOut', iso: null }
  const badge = getCustomerHealthBadge(customer)
  if (!badge) return null
  return badge.tone === 'bad'
    ? { tone: 'bad', kind: 'invalid', iso: null }
    : { tone: 'warn', kind: 'landline', iso: null }
}

/** First name only — the banners address the contact by first name. */
export function firstName(name?: string | null): string {
  return name?.trim().split(/\s+/)[0] ?? ''
}
